import React, { useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import fscreen from 'fscreen';
import FocusLock from 'react-focus-lock';
import { css } from '@compiled/react';
import { graphql, useFragment, type GraphQLTaggedNode } from 'react-relay';

import Portal from '@atlaskit/portal/portal';
import Spinner from '@atlaskit/spinner/spinner';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import { token } from '@atlaskit/tokens';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import {
	getEmojiFromPublishedPageProperties,
	getPublishedEmojiId,
} from '@confluence/emoji-title/entry-points/transformer.ts';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import { PageContentRenderer } from '@confluence/content-renderer/entry-points/PageContentRenderer.ts';
import { LazyQRCodeGenerator } from '@confluence/qr-code-generator';
import { LegacyMacroRendererContext } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/LegacyMacroRendererContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { setUserAgent, isFirefox } from '@confluence/browser-helper';
import {
	EMOJI_SIZE_LARGE,
	EMOJI_SIZE_MEDIUM,
} from '@confluence/emoji-title/entry-points/emojiTitleConstants.ts';
import { useContentType } from '@confluence/page-context';
import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

// eslint-disable-next-line import/no-cycle
import { useFullscreenMode } from './useFullscreenMode.tsx';
import { useLockBodyScroll } from './useLockBodyScroll.tsx';
import { useHideFlags } from './useHideFlags.tsx';
import { HeaderPresenterMode } from './HeaderPresenterMode.tsx';
import { HeaderImage } from './HeaderImage.tsx';
import { ErrorFlag } from './ErrorFlag.tsx';
import {
	FlagsContainer,
	PresenterModePortalContainer,
	PresenterModeZoomContainer,
	LoadingContainer,
	SpinnerContainer,
	TitleContainer,
	EmojiWrapper,
	TitleTextWrapper,
} from './presenterModeStyles.tsx';
import { usePresenterModeContext } from './PresenterModeContext.tsx';
import { useIsTitleCenterAligned } from './utilities.ts';
import type {
	PresenterModePortalPresenterModeContentTitleQuery$data,
	PresenterModePortalPresenterModeContentTitleQuery$variables,
} from './__generated__/PresenterModePortalPresenterModeContentTitleQuery.graphql.ts';
import type {
	PresenterModePortalPresenterModeCoverPictureQuery$data,
	PresenterModePortalPresenterModeCoverPictureQuery$variables,
} from './__generated__/PresenterModePortalPresenterModeCoverPictureQuery.graphql.ts';
import type { PresenterModePortal_content$key } from './__generated__/PresenterModePortal_content.graphql.ts';

const i18n = defineMessages({
	genericErrorMessage: {
		id: 'presentation-mode.portal.generic.error.message',
		description:
			'The text is shown as an error message in the presentation mode portal when an unexpected generic error occurs and the presentation cannot be displayed.',
		defaultMessage: 'Something’s not right. Refresh and try again.',
	},
});

const presenterModeContainerStyle = css({
	width: '100%',
});

const spacerStyle = css({
	height: '50px',
});

/* eslint-disable @atlaskit/design-system/use-tokens-typography */
const titleFixedSpacingStyle = css({
	fontWeight: 500,
	margin: 0,
	color: token('color.text'),
	wordBreak: 'break-word',
	overflowWrap: 'break-word',
	minHeight: token('space.500'),
	paddingTop: token('space.075'),
	display: 'block',
	fontSize: 'calc(64px / 1.3)',
	lineHeight: 'calc(76px / 1.3)',
	'@media (min-width: 1536px)': {
		fontSize: 'calc(72px / 1.5)',
		lineHeight: 'calc(84px / 1.5)',
	},
	'@media (min-width: 2560px)': {
		fontSize: 'calc(84px / 2)',
		lineHeight: 'calc(96px / 2)',
	},
});
/* eslint-enable @atlaskit/design-system/use-tokens-typography */

type PresenterModePortalProps = {
	contentId: string;
	spaceKey: string;
	setFlagsVisibility: (areFlagsVisible: boolean) => Promise<void>;
};

export const PresenterModeZoomContainerId = 'presenter-mode-zoom-container';
export const PresenterModeContainerId = 'presenter-mode-container';
export const PresenterModePortalContainerId = 'presenter-mode-portal-container';
export const PresenterModePageTitleId = 'presenter-mode-title-text';
export const PresenterModeContentContainerId = 'presenter-mode-content-container';
export const PresenterModeBottomSpacerId = 'presenter-mode-content-container-bottom-spacer';

setUserAgent();

const legacyMacroRendererContextValue = { shouldRefetchMacroQuery: true };
const presenterModeStickyHeaders = { offsetTop: 0 };
type PresenterModeEmoji = React.ComponentProps<typeof LazyEmojiComponentLoader>['emoji'];

const PresenterModePortalContentFragment = graphql`
	fragment PresenterModePortal_content on Content {
		title
		properties(
			keys: [
				"cover-picture-id-published"
				"emoji-title-published"
				"editor"
				"import-source"
				"imported-page-modified-date"
			]
		) {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
	}
`;

const PresenterModePortalPresenterModeContentTitleQuery: GraphQLTaggedNode = graphql`
	query PresenterModePortalPresenterModeContentTitleQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				title
				emojiTitlePublished: properties(key: "emoji-title-published") {
					nodes {
						id
						key
						value
					}
				}
			}
		}
	}
`;

const PresenterModePortalPresenterModeCoverPictureQuery: GraphQLTaggedNode = graphql`
	query PresenterModePortalPresenterModeCoverPictureQuery($contentId: ID, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				properties(keys: ["cover-picture-id-published"]) {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
			}
		}
	}
`;

type PresenterModePortalRenderProps = {
	contentId: string;
	spaceKey: string;
	isCustomCursorActive: boolean;
	contentRendered: boolean;
	onSwitchCursor: (newValue: boolean) => void;
	loading: boolean;
	coverPictureProperty: any;
	error: unknown;
	title: string;
	emojiTitle: PresenterModeEmoji;
	contentData: ContentUnifiedQueryType | ContentBlogUnifiedQueryType | undefined;
	editorContent?: DocNode;
	onRendered: () => void;
	isTitleCenterAligned: boolean;
	viewportWidth: number;
	intl: ReturnType<typeof useIntl>;
};

const PresenterModePortalRender = ({
	contentId,
	spaceKey,
	isCustomCursorActive,
	contentRendered,
	onSwitchCursor,
	loading,
	coverPictureProperty,
	error,
	title,
	emojiTitle,
	contentData,
	editorContent,
	onRendered,
	isTitleCenterAligned,
	viewportWidth,
	intl,
}: PresenterModePortalRenderProps): React.JSX.Element => {
	const hasCoverPicture = !!coverPictureProperty;
	const hasEmoji = !!emojiTitle;

	return (
		<Portal zIndex={519}>
			<FocusLock autoFocus={false} returnFocus={false}>
				<TransparentErrorBoundary attribution={Attribution.COMMENTS}>
					<PresenterModePortalContainer
						id={PresenterModePortalContainerId}
						isCustomCursorActive={isCustomCursorActive}
					>
						<HeaderPresenterMode
							contentRendered={contentRendered}
							onSwitchCursorClick={onSwitchCursor}
							contentId={contentId}
							spaceKey={spaceKey}
						/>
						<div css={presenterModeContainerStyle} id={PresenterModeContainerId}>
							{loading ? (
								<LoadingContainer data-testid="presenter-mode-spinner">
									<SpinnerContainer>
										<Spinner size="large" interactionName="presenter-mode-portal" />
									</SpinnerContainer>
								</LoadingContainer>
							) : (
								<>
									<HeaderImage contentId={contentId} coverPictureProperty={coverPictureProperty} />
									<PresenterModeZoomContainer
										data-testid="presenter-mode-zoom-container"
										id={PresenterModeZoomContainerId}
										isFirefox={isFirefox()}
									>
										<FlagsContainer>
											{Boolean(error) && (
												<ErrorFlag title={intl.formatMessage(i18n.genericErrorMessage)} />
											)}
										</FlagsContainer>
										<TitleContainer
											id={PresenterModePageTitleId}
											data-testid="presenter-mode-title-container"
											hasCoverPicture={hasCoverPicture}
											isTitleCenterAligned={isTitleCenterAligned}
											hasEmoji={hasEmoji}
											width={viewportWidth}
										>
											{emojiTitle && (
												<EmojiWrapper hasCoverPicture={hasCoverPicture}>
													<span>
														<LazyEmojiComponentLoader
															emoji={emojiTitle}
															height={hasCoverPicture ? EMOJI_SIZE_LARGE : EMOJI_SIZE_MEDIUM}
															hasCoverPicture={hasCoverPicture}
															isPageTitleComponent
															context="presenterMode"
															showTooltip
															renderResourcedEmoji
														/>
													</span>
												</EmojiWrapper>
											)}
											<TitleTextWrapper
												data-testId="titleTextWrapper"
												shouldAddLeftPadding={hasEmoji && !isTitleCenterAligned && !hasCoverPicture}
											>
												{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
												<h1 css={titleFixedSpacingStyle}>{title}</h1>
											</TitleTextWrapper>
										</TitleContainer>
										<LegacyMacroRendererContext.Provider value={legacyMacroRendererContextValue}>
											<PageContentRenderer
												contentId={contentId}
												document={editorContent}
												data={contentData}
												onRendered={onRendered}
												hasInlineComments={false}
												appearance="full-page"
												stickyHeaders={presenterModeStickyHeaders}
												spaceKey={spaceKey}
												isPresentational
											/>
										</LegacyMacroRendererContext.Provider>
										<div css={spacerStyle} id={PresenterModeBottomSpacerId} />
									</PresenterModeZoomContainer>
								</>
							)}
						</div>
						<LazyQRCodeGenerator contentId={contentId} spaceKey={spaceKey} />
					</PresenterModePortalContainer>
				</TransparentErrorBoundary>
			</FocusLock>
		</Portal>
	);
};

type PresenterModePortalDataProps = Omit<
	PresenterModePortalRenderProps,
	'loading' | 'coverPictureProperty' | 'error' | 'title' | 'emojiTitle'
> & {
	contentNode: PresenterModePortal_content$key | null;
	contentLoading: boolean;
	contentUnifiedError: unknown;
};

type PresenterModePortalQueryDataProps = Omit<PresenterModePortalDataProps, 'contentNode'>;

const PresenterModePortalFragmentData = ({
	contentNode,
	contentLoading,
	contentUnifiedError,
	...props
}: PresenterModePortalDataProps): React.JSX.Element => {
	const fragmentData = useFragment(PresenterModePortalContentFragment, contentNode);
	const emojiTitle = getEmojiFromPublishedPageProperties(fragmentData);
	const coverPictureProperty = fragmentData?.properties?.nodes?.find(
		(node) => node?.key === 'cover-picture-id-published',
	);

	return (
		<PresenterModePortalRender
			{...props}
			loading={contentLoading}
			coverPictureProperty={coverPictureProperty}
			error={contentUnifiedError}
			title={fragmentData?.title ?? ''}
			emojiTitle={emojiTitle}
		/>
	);
};

const PresenterModePortalQueryData = ({
	contentId,
	contentLoading,
	contentUnifiedError,
	...props
}: PresenterModePortalQueryDataProps): React.JSX.Element => {
	const {
		data: titleData,
		loading: titleLoading,
		error: contentTitleError,
	} = useQueryInterop<
		PresenterModePortalPresenterModeContentTitleQuery$data,
		PresenterModePortalPresenterModeContentTitleQuery$variables
	>(PresenterModePortalPresenterModeContentTitleQuery, {
		fetchPolicy: 'cache-first',
		variables: {
			contentId,
		},
	});
	const {
		data: coverPictureQueryData,
		loading: coverPictureLoading,
		error: presenterModeCoverPictureError,
	} = useQueryInterop<
		PresenterModePortalPresenterModeCoverPictureQuery$data,
		PresenterModePortalPresenterModeCoverPictureQuery$variables
	>(PresenterModePortalPresenterModeCoverPictureQuery, {
		fetchPolicy: 'cache-first',
		context: { single: true },
		variables: {
			contentId,
			status: ['current'],
		},
	});
	const coverPictureProperty =
		coverPictureQueryData?.content?.nodes?.[0]?.properties?.nodes?.[0] ?? undefined;

	return (
		<PresenterModePortalRender
			{...props}
			contentId={contentId}
			loading={contentLoading || titleLoading || coverPictureLoading}
			coverPictureProperty={coverPictureProperty}
			error={contentTitleError || contentUnifiedError || presenterModeCoverPictureError}
			title={titleData?.content?.nodes?.[0]?.title ?? ''}
			emojiTitle={getPublishedEmojiId(titleData)}
		/>
	);
};

/**
 * This component encapsulates the actual presenter mode experience. It's responsible
 * for rendering the nav bar and the content itself, which it gets via GraphQL.
 * Everything is placed within a Portal component which is rendered on top of the page.
 */
export const PresenterModePortal = ({
	contentId,
	spaceKey,
	setFlagsVisibility,
}: PresenterModePortalProps): React.JSX.Element => {
	const [isCustomCursorActive, setIsCustomCursorActive] = useState(true);
	const [contentRendered, setContentRendered] = useState(false);
	const [editorContent, setEditorContent] = useState<DocNode>();
	const { isBrowserFullscreen } = usePresenterModeContext();
	const [{ fullPageEditorPresetAPI: editorApi }] = useFullPageEditorApi();
	const intl = useIntl();

	const handleSwitchCursor = (newValue: boolean) => setIsCustomCursorActive(newValue);

	// Prevent scrolling of content underneath the portal
	useLockBodyScroll();

	// Handles entering/exiting fullscreen mode
	useFullscreenMode();

	useEffect(() => {
		editorApi?.core.actions.requestDocument((doc) => {
			setEditorContent(doc as DocNode);
		});
	}, [editorApi]);

	useEffect(() => {
		if (isBrowserFullscreen) {
			fscreen.exitFullscreen();
		}
	}, [isBrowserFullscreen]);

	// Handles hiding flags while in Presenter Mode
	useHideFlags(setFlagsVisibility);

	const [contentType] = useContentType();
	const isBlogpost = contentType === 'blogpost';
	const skipContentUnifiedQuery = !contentId || !spaceKey;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	const pageContentUnifiedResult = useQueryInterop<
		ContentUnifiedQueryType,
		ContentUnifiedQueryVariablesType
	>(ContentUnifiedQueryArtifact, {
		fetchPolicy: 'cache-first',
		context: { single: true },
		skip: skipContentUnifiedQuery || isBlogpost,
		variables: {
			contentId,
			spaceKey,
			isSSR,
			versionOverride: null,
		},
	});

	const blogContentUnifiedResult = useQueryInterop<
		ContentBlogUnifiedQueryType,
		ContentBlogUnifiedQueryVariablesType
	>(ContentBlogUnifiedQueryArtifact, {
		fetchPolicy: 'cache-first',
		context: { single: true },
		skip: skipContentUnifiedQuery || !isBlogpost,
		variables: {
			contentId,
			spaceKey,
			isSSR,
			versionOverride: null,
		},
	});

	const {
		data: contentData,
		loading: contentLoading,
		error: contentUnifiedError,
	} = isBlogpost ? blogContentUnifiedResult : pageContentUnifiedResult;
	const contentNode = contentData?.content?.nodes?.[0];

	const handleOnRenderedEvent = (): void => setContentRendered(true);

	const isTitleCenterAligned = useIsTitleCenterAligned(contentId);

	const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

	useEffect(() => {
		const handleResize = () => {
			setViewportWidth(window.innerWidth);
		};

		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const sharedProps = {
		contentId,
		spaceKey,
		isCustomCursorActive,
		contentRendered,
		onSwitchCursor: handleSwitchCursor,
		contentData,
		editorContent,
		onRendered: handleOnRenderedEvent,
		isTitleCenterAligned,
		viewportWidth,
		intl,
	};

	if (
		!StatsigConfigurations.isKillSwitchOn('confluence_frontend_view_page_relay_fragment_dedup') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return (
			<PresenterModePortalFragmentData
				{...sharedProps}
				contentNode={contentNode as PresenterModePortal_content$key | null}
				contentLoading={contentLoading}
				contentUnifiedError={contentUnifiedError}
			/>
		);
	}

	return (
		<PresenterModePortalQueryData
			{...sharedProps}
			contentLoading={contentLoading}
			contentUnifiedError={contentUnifiedError}
		/>
	);
};
